import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import {
  LA88_COVERAGE_FIGURE_ASSETS,
  LA88_COVERAGE_FIGURE_HEIGHT,
  LA88_COVERAGE_FIGURE_WIDTH,
  shouldShowLaCountyInventoryCoverage,
} from '../../config/laCountyInventoryCoverage';
import './LaCountyGhgInventoryCoverageSection.css';

export type La88CoverageView = 'published' | 'climatePlans';

/** Persist toggle across eligible LA locations for the browser session (presentation flow). */
let sessionCoverageView: La88CoverageView = 'published';

export interface LaCountyGhgInventoryCoverageSectionProps {
  location: string;
}

export const LaCountyGhgInventoryCoverageSection: React.FC<
  LaCountyGhgInventoryCoverageSectionProps
> = ({ location }) => {
  const titleId = useId();
  const tabPublishedId = useId();
  const tabClimateId = useId();
  const panelId = useId();
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const expandBtnRef = useRef<HTMLButtonElement>(null);

  const [eligible, setEligible] = useState(false);
  const [view, setView] = useState<La88CoverageView>(sessionCoverageView);
  const [expanded, setExpanded] = useState(false);
  const [imagesReady, setImagesReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const evaluate = async () => {
      await supabaseLocationService.initialize();
      const geographyType = supabaseLocationService.getLocationType(location);
      const countyFromMetadata =
        geographyType === 'city' ? supabaseLocationService.getCountyForCity(location) : null;
      let geofips: number | null = null;
      try {
        geofips = await supabaseLocationService.getGeofips(location);
      } catch {
        geofips = null;
      }
      if (cancelled) return;
      setEligible(
        shouldShowLaCountyInventoryCoverage({
          locationName: location,
          countyFromMetadata,
          geographyType,
          geofips,
        })
      );
    };

    void evaluate();
    return () => {
      cancelled = true;
    };
  }, [location]);

  useEffect(() => {
    let cancelled = false;
    const urls = [LA88_COVERAGE_FIGURE_ASSETS.published.src, LA88_COVERAGE_FIGURE_ASSETS.climatePlans.src];
    let loaded = 0;
    const mark = () => {
      loaded += 1;
      if (!cancelled && loaded >= urls.length) setImagesReady(true);
    };
    urls.forEach((src) => {
      const img = new Image();
      img.onload = mark;
      img.onerror = mark;
      img.src = src;
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectView = useCallback((next: La88CoverageView) => {
    sessionCoverageView = next;
    setView(next);
  }, []);

  const openExpand = useCallback(() => {
    setExpanded(true);
  }, []);

  const closeExpand = useCallback(() => {
    setExpanded(false);
    // Restore focus to expand control after dialog closes
    window.setTimeout(() => expandBtnRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (!expanded) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeExpand();
      }
    };
    window.addEventListener('keydown', onKey);
    window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [expanded, closeExpand]);

  if (!eligible) return null;

  const active = LA88_COVERAGE_FIGURE_ASSETS[view];
  const aspect = `${LA88_COVERAGE_FIGURE_WIDTH} / ${LA88_COVERAGE_FIGURE_HEIGHT}`;

  return (
    <div className="publication-analysis-strip la88-coverage-strip">
      <div className="publication-analysis-strip-inner">
        <section
          className="la88-coverage-section"
          aria-labelledby={titleId}
          data-images-ready={imagesReady ? 'true' : 'false'}
        >
      <h2 id={titleId}>GHG inventory coverage across LA County</h2>

      <div className="la88-coverage-toolbar">
        <div
          className="la88-coverage-segmented"
          role="tablist"
          aria-label="Inventory coverage view"
        >
          <button
            type="button"
            role="tab"
            id={tabPublishedId}
            aria-selected={view === 'published'}
            aria-controls={panelId}
            tabIndex={view === 'published' ? 0 : -1}
            className={`la88-coverage-segment${view === 'published' ? ' is-active' : ''}`}
            onClick={() => selectView('published')}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                e.preventDefault();
                selectView('climatePlans');
                document.getElementById(tabClimateId)?.focus();
              }
            }}
          >
            Published inventories
          </button>
          <button
            type="button"
            role="tab"
            id={tabClimateId}
            aria-selected={view === 'climatePlans'}
            aria-controls={panelId}
            tabIndex={view === 'climatePlans' ? 0 : -1}
            className={`la88-coverage-segment${view === 'climatePlans' ? ' is-active' : ''}`}
            onClick={() => selectView('climatePlans')}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                e.preventDefault();
                selectView('published');
                document.getElementById(tabPublishedId)?.focus();
              }
            }}
          >
            With ClimatePlans
          </button>
        </div>

        <div className="la88-coverage-actions">
          <button
            type="button"
            className="la88-coverage-action-btn"
            ref={expandBtnRef}
            onClick={openExpand}
          >
            Expand figure
          </button>
          <a
            className="la88-coverage-action-btn la88-coverage-action-btn--link"
            href={active.src}
            download={active.downloadName}
          >
            Download PNG
          </a>
        </div>
      </div>

      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={view === 'published' ? tabPublishedId : tabClimateId}
        className="la88-coverage-figure-frame"
        style={{ aspectRatio: aspect }}
      >
        <div className="la88-coverage-figure-stack" aria-live="polite">
          <img
            src={LA88_COVERAGE_FIGURE_ASSETS.published.src}
            alt={LA88_COVERAGE_FIGURE_ASSETS.published.alt}
            className={`la88-coverage-figure${view === 'published' ? ' is-visible' : ''}`}
            width={LA88_COVERAGE_FIGURE_WIDTH}
            height={LA88_COVERAGE_FIGURE_HEIGHT}
            decoding="async"
            draggable={false}
          />
          <img
            src={LA88_COVERAGE_FIGURE_ASSETS.climatePlans.src}
            alt={LA88_COVERAGE_FIGURE_ASSETS.climatePlans.alt}
            className={`la88-coverage-figure${view === 'climatePlans' ? ' is-visible' : ''}`}
            width={LA88_COVERAGE_FIGURE_WIDTH}
            height={LA88_COVERAGE_FIGURE_HEIGHT}
            decoding="async"
            draggable={false}
          />
        </div>
      </div>

      <p className="la88-coverage-sources">
        Sources: Michael Boswell, California CAP Database (Nov. 2025), supplemented by EN-01 CAP
        extraction ledgers. ClimatePlans coverage reflects stored records plus confirmed Glendale
        coverage.
      </p>
      <p className="la88-coverage-note">
        Published inventory years shown are those documented in the available sources; additional
        inventories may exist. Coverage does not establish accuracy or independent validation.
      </p>

      {expanded ? (
        <div
          className="la88-coverage-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeExpand();
          }}
        >
          <div
            className="la88-coverage-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${titleId}-modal`}
          >
            <div className="la88-coverage-modal-header">
              <h3 id={`${titleId}-modal`}>GHG inventory coverage across LA County</h3>
              <button
                type="button"
                className="la88-coverage-modal-close"
                ref={closeBtnRef}
                onClick={closeExpand}
                aria-label="Close expanded figure"
              >
                Close
              </button>
            </div>
            <div className="la88-coverage-modal-body">
              <img
                src={active.src}
                alt={active.alt}
                className="la88-coverage-modal-img"
                width={LA88_COVERAGE_FIGURE_WIDTH}
                height={LA88_COVERAGE_FIGURE_HEIGHT}
              />
            </div>
            <div className="la88-coverage-modal-footer">
              <a
                className="la88-coverage-action-btn la88-coverage-action-btn--link"
                href={active.src}
                download={active.downloadName}
              >
                Download PNG
              </a>
              <p className="la88-coverage-sources la88-coverage-sources--modal">
                Sources: Michael Boswell, California CAP Database (Nov. 2025), supplemented by EN-01
                CAP extraction ledgers. ClimatePlans coverage reflects stored records plus confirmed
                Glendale coverage.
              </p>
            </div>
          </div>
        </div>
      ) : null}
        </section>
      </div>
    </div>
  );
};

export default LaCountyGhgInventoryCoverageSection;
